// 画像圧縮関数の戻り値の型
interface CompressResult {
    file: File;
    originalSize: number;
    compressedSize: number;
    compressionRatio: number;
    width: number;
    height: number;
}

// 画像圧縮関数 - パラメータを固定値に設定
export const compressImage = async (file: File): Promise<CompressResult> => {
    // 固定パラメータ
    const maxWidthOrHeight = 1600;  // 最大幅または高さ（ピクセル）
    const quality = 0.3;            // JPEGの品質（0〜1）- 30%の画質
    const maxSizeMB = 2;            // 最大ファイルサイズ（MB）

    return new Promise<CompressResult>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            const img = new Image();
            img.onload = () => {
                // 元のアスペクト比を維持しながらリサイズ
                let width = img.width;
                let height = img.height;

                if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
                    if (width > height) {
                        height *= maxWidthOrHeight / width;
                        width = maxWidthOrHeight;
                    } else {
                        width *= maxWidthOrHeight / height;
                        height = maxWidthOrHeight;
                    }
                }

                // Canvasを作成して画像を描画
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }
                ctx.drawImage(img, 0, 0, width, height);

                // ファイルの種類を確認（デフォルトはJPEG）
                let mimeType = 'image/jpeg';
                if (file.type === 'image/png') {
                    mimeType = 'image/png';
                } else if (file.type === 'image/webp') {
                    mimeType = 'image/webp';
                }

                // Canvasからデータを取得
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob conversion failed'));
                            return;
                        }

                        // 結果のファイルを作成
                        const resultFile = new File(
                            [blob],
                            file.name,
                            { type: mimeType }
                        );

                        // 圧縮後のサイズが設定した最大サイズより大きい場合はさらに品質を下げる
                        if (resultFile.size > maxSizeMB * 1024 * 1024 && mimeType === 'image/jpeg' && quality > 0.2) {
                            // 品質を下げて再度圧縮（再帰呼び出しではなく内部でキャンバスを再作成）
                            canvas.toBlob(
                                (smallerBlob) => {
                                    if (!smallerBlob) {
                                        reject(new Error('Canvas to Blob conversion failed'));
                                        return;
                                    }

                                    // さらに圧縮したファイルを作成
                                    const smallerFile = new File(
                                        [smallerBlob],
                                        file.name,
                                        { type: mimeType }
                                    );

                                    resolve({
                                        file: smallerFile,
                                        originalSize: file.size,
                                        compressedSize: smallerFile.size,
                                        compressionRatio: file.size / smallerFile.size,
                                        width,
                                        height
                                    });
                                },
                                mimeType,
                                quality - 0.1
                            );
                        } else {
                            resolve({
                                file: resultFile,
                                originalSize: file.size,
                                compressedSize: resultFile.size,
                                compressionRatio: file.size / resultFile.size,
                                width,
                                height
                            });
                        }
                    },
                    mimeType,
                    quality
                );
            };
            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            if (event.target?.result) {
                img.src = event.target.result as string;
            } else {
                reject(new Error('FileReader result is null'));
            }
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
    });
};